import {
	DATE_YMD,
	IS_NOT_EMPTY,
	TO_CENTS,
	Owner,
	ANNUAL_VOLUME_DISPLAY,
} from 'cargo-chief-commons';
import { SORT_FIELD_MAPS, PROJECT_FIELDS, PROJECT_STAGES } from '../../project/projects-list/constants';
import { OPERATORS } from '../../../shared/tabbed-table/constants';
import { environment } from '@environment/environment';

export function buildRequest(
	accountId: number,
	annualVolume: boolean,
	filtersList: any[],
	page: number,
	pageSize: number,
	sortColumn: string,
	sortOrder: string,
	sortList: any[],
	userId: number,
	activeUsersList: Owner[],
	enforcedProjectTypes?: string[] // Only used when feature flag is ON
) {
	let request: any = {
		accountId,
		page,
		pageSize,
		sortColumn,
		sortOrder,
		annualVolume,
		userId,
	};

	// set filters
	if (filtersList?.length) {
		let filtersRequestList = [],
			locations = [],
			savedTypeFilter = null;
		filtersList.forEach((filter) => {
			const condition = IS_NOT_EMPTY(filter.filterCondition) ? filter.filterCondition : 'and',
				operator = filter.operator,
				startValue = filter.startValue,
				endValue = filter.endValue;
			if (filter.filterField === 'Name') {
				filtersRequestList.push({
					field: 'name',
					value: filter.filterText,
					condition,
				});
			} else if (filter.filterField === PROJECT_FIELDS.START_DATE) {
				filtersRequestList.push({
					field: 'dueDate',
					value: filter.filterText,
					condition,
					startValue: DATE_YMD(filter.startValue),
					endValue: DATE_YMD(filter.endValue),
					operator,
				});
			} else if (filter.filterField === PROJECT_FIELDS.ORIGIN_CITY) {
				filtersRequestList.push({
					field: 'originCity',
					value: filter.filterText,
					condition,
					startValue,
					endValue,
					operator,
				});
			} else if (filter.filterField === PROJECT_FIELDS.ORIGIN_STATE) {
				filtersRequestList.push({
					field: 'originState',
					value: filter.filterText.join(','),
					condition,
					startValue,
					endValue,
					operator,
				});
			} else if (filter.filterField === PROJECT_FIELDS.DESTINATION_CITY) {
				filtersRequestList.push({
					field: 'destinationCity',
					value: filter.filterText,
					condition,
					startValue,
					endValue,
					operator,
				});
			} else if (filter.filterField === PROJECT_FIELDS.DESTINATION_STATE) {
				filtersRequestList.push({
					field: 'destinationState',
					value: filter.filterText.join(','),
					condition,
					startValue,
					endValue,
					operator,
				});
			}

			if (filter.filterField === PROJECT_FIELDS.CUSTOMER) {
				filtersRequestList.push({
					field: 'customer',
					value: filter.filterText.join(','),
					condition,
					startValue,
					endValue,
					operator,
				});
			} else if (filter.filterField === PROJECT_FIELDS.VOLUME) {
				if (annualVolume) {
					filtersRequestList.push({
						field: 'volume',
						value: filter.filterText,
						startValue: ANNUAL_VOLUME_DISPLAY(filter.startValue),
						endValue: ANNUAL_VOLUME_DISPLAY(filter.endValue),
						condition,
						operator,
					});
				} else {
					filtersRequestList.push({
						field: 'volume',
						value: filter.filterText,
						startValue: filter.startValue,
						endValue: filter.endValue,
						condition,
						operator,
					});
				}
			} else if (filter.filterField === PROJECT_FIELDS.EQUIPMENT) {
				filtersRequestList.push({
					// CN-8498: backend (carrier-hub V2ListProjectsService:43,175) matches
					// the field name 'equipmentTypes', not 'equipment'. Sending
					// 'equipment' silently drops the filter — the list returns
					// unfiltered. Match the convention used by the original Projects
					// list (v2/project/projects-list/build-projects-request.ts:133)
					// and the Loads list.
					field: 'equipmentTypes',
					value: filter.filterText.join(','),
					condition,
					startValue,
					endValue,
					operator,
				});
			} else if (filter.filterField === PROJECT_FIELDS.COMMODITY) {
				filtersRequestList.push({
					field: 'commodity',
					value: filter.filterText.join(','),
					condition,
					startValue,
					endValue,
					operator,
				});
			} else if (filter.filterField === PROJECT_FIELDS.WEIGHT) {
				filtersRequestList.push({
					field: 'weight',
					value: filter.filterText,
					startValue: filter.startValue,
					endValue: filter.endValue,
					condition,
					operator,
				});
			} else if (filter.filterField === PROJECT_FIELDS.MAX_BUY) {
				filtersRequestList.push({
					field: 'maxBuy',
					value: filter.filterText,
					startValue: TO_CENTS(filter.startValue),
					endValue: TO_CENTS(filter.endValue),
					condition,
					operator,
				});
			} else if (filter.filterField === PROJECT_FIELDS.TARGET_CARRIER_RATE) {
				filtersRequestList.push({
					field: 'targetCarrierRate',
					value: filter.filterText,
					startValue: TO_CENTS(filter.startValue),
					endValue: TO_CENTS(filter.endValue),
					condition,
					operator,
				});
			} else if (filter.filterField === PROJECT_FIELDS.OFFER_RATE) {
				filtersRequestList.push({
					field: 'offerRate',
					value: filter.filterText,
					startValue: TO_CENTS(filter.startValue),
					endValue: TO_CENTS(filter.endValue),
					condition,
					operator,
				});
			} else if (filter.filterField === PROJECT_FIELDS.PROCURED_CARRIERS) {
				filtersRequestList.push({
					field: 'procuredCarriers',
					value: filter.filterText,
					startValue: filter.startValue,
					endValue: filter.endValue,
					condition,
					operator,
				});
			} else if (filter.filterField === PROJECT_FIELDS.ASSIGNED_REPS) {
				filtersRequestList.push({
					field: 'assigneeId',
					value: filter.filterText.join(','),
					condition,
					operator,
				});
			} else if (filter.filterField === PROJECT_FIELDS.ORIGIN) {
				filtersRequestList.push({
					field: 'originLocation',
					value: filter.filterText,
					condition,
					startValue,
					endValue,
					operator,
				});
			} else if (filter.filterField === PROJECT_FIELDS.DESTINATION) {
				filtersRequestList.push({
					field: 'destinationLocation',
					value: filter.filterText,
					condition,
					startValue,
					endValue,
					operator,
				});
			} else if (filter.filterField === PROJECT_FIELDS.STAGE) {
				filtersRequestList.push({
					field: 'stage',
					value: filter.filterText.join(','),
					condition,
					startValue,
					endValue,
					operator,
				});
			} else if (filter.filterField === PROJECT_FIELDS.TYPE) {
				// Store the saved type filter to merge with enforced types later
				savedTypeFilter = {
					field: 'types',
					value: filter.filterText.join(','),
					condition,
					startValue,
					endValue,
					operator,
				};
			} else if (filter.filterField === PROJECT_FIELDS.ARCHIVED) {
				filtersRequestList.push({
					field: 'archived',
					value: filter.filterText,
					condition,
					startValue,
					endValue,
					operator,
				});
			} else if (filter.filterField === PROJECT_FIELDS.REVENUE_CODE) {
				filtersRequestList.push({
					field: 'revenueCode',
					value: filter.filterText.join(','),
					condition,
					startValue,
					endValue,
					operator,
				});
			} else if (filter.filterField === PROJECT_FIELDS.LANE) {
				locations.push(filter.filterText);
			} else if (filter.filterField === PROJECT_FIELDS.CARRIER) {
				filtersRequestList.push({
					field: 'carrier',
					value: filter.filterText.join(','),
					condition,
					startValue,
					endValue,
					operator,
				});
			}
		});

		// Handle type filtering - merge saved type filter with enforced types
		if (enforcedProjectTypes?.length && environment.FEATURE_FLAGS.SPLIT_PROJECTS_MENU_ENABLED) {
			if (savedTypeFilter) {
				// Merge saved type filter with enforced types
				const savedTypes = savedTypeFilter.value.split(',');
				const mergedTypes = [...new Set([...savedTypes, ...enforcedProjectTypes])];
				filtersRequestList.push({
					field: 'types',
					value: mergedTypes.join(','),
					condition: 'and',
					operator: '=',
				});
			} else {
				// Only apply enforced project type filter
				filtersRequestList.push({
					field: 'types',
					value: enforcedProjectTypes.join(','),
					condition: 'and',
					operator: '=',
				});
			}
		} else if (savedTypeFilter) {
			// No enforced types, just use saved type filter
			filtersRequestList.push(savedTypeFilter);
		}

		// Note: Stage filtering for tabs would need to be implemented 
		// with proper stage values when requirements are clarified

		// if location filters array is not empty
		if (locations.length > 0) {
			filtersRequestList.push({
				field: 'location',
				value: locations.join(','),
				condition: 'and',
			});
		}

		request.filtersList = JSON.stringify(filtersRequestList);
		request.locations = JSON.stringify(locations);
	} else {
		// Only apply enforced project type filter when feature flag is ON
		if (enforcedProjectTypes?.length && environment.FEATURE_FLAGS.SPLIT_PROJECTS_MENU_ENABLED) {
			request.filtersList = JSON.stringify([
				{
					field: 'types',
					value: enforcedProjectTypes.join(','),
					condition: 'and',
					operator: '=',
				},
			]);
		}
	}

	// set sorts
	if (sortList?.length) {
		let sortRequest = [];
		sortList.forEach((sort) => {
			if (sort.sortField === PROJECT_FIELDS.CUSTOMER) {
				let fieldValues = '';
				if (sort.direction?.toLowerCase() === 'asc') {
					fieldValues = 'name,dot,id';
				} else {
					fieldValues = 'id,dot,name';
				}
				sortRequest.push({
					field: SORT_FIELD_MAPS[sort.sortField],
					direction: sort.direction,
					fieldValues,
				});
			} else if (sort.sortField === PROJECT_FIELDS.STAGE) {
				let fieldValues = '';
				if (sort.direction?.toLowerCase() === 'asc') {
					fieldValues = [...PROJECT_STAGES].sort(
						(a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase())
					)
						.map((stage) => stage.type)
						.join(',');
				} else {
					fieldValues = [...PROJECT_STAGES].sort(
						(a, b) => b.label.toLowerCase().localeCompare(a.label.toLowerCase())
					)
						.map((stage) => stage.type)
						.join(',');
				}
				sortRequest.push({
					field: SORT_FIELD_MAPS[sort.sortField],
					direction: sort.direction,
					fieldValues,
				});
			} else {
				sortRequest.push({
					field: SORT_FIELD_MAPS[sort.sortField],
					direction: sort.direction,
				});
			}
		});
		request.sortList = JSON.stringify(sortRequest);
	}

	return request;
}
